import React, { Component, useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFlag, faHandHoldingDollar, faShareNodes } from "@fortawesome/free-solid-svg-icons";

import Comments from "$components/comments";
import Feedback from "$components/feedback";
import FeedbackUndo from "$components/feedback/feedback-undo";
import InfoTable from "$components/info-table";
import LargeNumberRange from "$components/table/cells/large-number-range";
import LinkInfoCell from "$components/info-table/cells/link-info-cell";
import NotificationBar from "$components/notification-bar";
import PotentialProjects from "../components/potential-projects";
import ProbabilityBubble from "$components/probability";
import RebelGroupButton from "$components/button/rebel-group-export";
import RebelGroupOpportunityForm from "$components/rebel-group/form";
import SalesforceButton from "$components/button/salesforce-export";
import SalesforceOpportunityForm from "$components/salesforce/form";
import ShareModal from "$components/modal/share-modal";
import TeamInfoCell from "$components/info-table/cells/team-info-cell";

import { fetchProject, ProjectDetailsStore } from "$stores/projectDetails";
import { useDocumentTitle } from "$utils/document-title";
import { getTerm } from "$stores/dictionary";
import { AppContextStore } from "$stores/app-context";
import { closeModal, initiateModal } from "$stores/modal";
import { setHelpContent } from "$stores/help";
import { HELP_POTENTIAL_PROJECT_DETAIL } from "src/pages/help/lib/help-contents";
import { capitalizeFirstLetter } from "$utils/text-helpers";
import { useAppInsightsContext } from "@microsoft/applicationinsights-react-js";

const AverageRevenueCell = ({ value, source, ...props }) => {
    if (source === "Foley") return <span>--</span>;
    return <LargeNumberRange value={value} {...props} />;
};

const buildInfoPropertiesMap = (showExperiencedTeamMembers, showProjectTypeGroup) => {
    return {
        clientName: { label: `${getTerm("parentClient")} Name`, Component: LinkInfoCell, additionalProps: { url: "/client/#{clientId}" }, colSpan: 2 },
        subClientName: { label: getTerm("subClient"), colSpan: 2 },
        clientPIC: { label: getTerm("subCrm"), Component: LinkInfoCell, colSpan: 2, additionalProps: { id: "#{crmId}", url: "/team/#{crmId}", crmId: "#{crmId}", isActive: "#{crmIsActive}", email: "#{crmEmail}" } },
        ...(showProjectTypeGroup && { projectTypeGroup: { label: getTerm("projectTypeGroup"), colSpan: 2 } }),
        serviceLine: { label: getTerm("serviceLine"), colSpan: showProjectTypeGroup ? 2 : 3 },
        industry: { label: getTerm("industry"), colSpan: showProjectTypeGroup ? 2 : 3 },
        averageRevenue: {
            label: <span data-tooltip={getTerm("tooltipAverageProjectRevenue")}>{getTerm("averageRevenue")} Size</span>,
            Component: AverageRevenueCell,
            additionalProps: { source: "#{source}", decimalPrecision: 0 },
            colSpan: 2,
        },
        probability: { label: <span data-tooltip={getTerm("tooltipExpansionProbability")}>Deal Probability</span>, Component: ProbabilityBubble, colSpan: 2 },
        ...(showExperiencedTeamMembers && {
            team: {
                label: <span data-tooltip={getTerm("tooltipExperiencedTeam")}>Experienced {getTerm("teamMembers")}</span>,
                Component: TeamInfoCell,
                colSpan: 2,
                additionalProps: { projectId: "#{projectId}", projectKey: "#{projectKey}", allowFeedback: true },
            },
        }),
    };
};

const PotentialProject = () => {
    useDocumentTitle(`Potential ${getTerm("project")}`);
    const appInsights = useAppInsightsContext();
    const { enableSalesForce, enableRebelGroup, enableShare, enableExperienceTeamMembers } = AppContextStore.useState((a) => {
        return { enableSalesForce: a.settings?.externalConnections?.salesforce, enableRebelGroup: a.settings?.externalConnections?.rebelGroupDemo, enableShare: a.settings?.externalConnections?.share, enableExperienceTeamMembers: a.settings?.sections?.experiencedTeamMembers || false };
    });
    const { comparableProjects, currentProject } = ProjectDetailsStore.useState((s) => {
        return { comparableProjects: s.comparables, currentProject: s.projectDetails };
    });
    const [infoPropertiesMap] = useState(buildInfoPropertiesMap(enableExperienceTeamMembers, !!currentProject.projectTypeGroup));

    useEffect(() => {
        setHelpContent(HELP_POTENTIAL_PROJECT_DETAIL);
    }, []);

    const triggerModal = (type) => {
        if (type === "salesforce") {
            initiateModal(
                SalesforceOpportunityForm,
                {
                    clientId: currentProject.subClientId,
                    currentProject: currentProject,
                    projectKey: currentProject.projectKey,
                    afterSubmit: onSalesforceSubmit,
                },
                { analyticsLabel: "Salesforce" }
            );
        }
        if (type === "rebelgroup") {
            initiateModal(
                RebelGroupOpportunityForm,
                {
                    currentProject: currentProject,
                    projectKey: currentProject.projectKey,
                    afterSubmit: onRebelGroupSubmit,
                },
                { analyticsLabel: "RebelGroup" }
            );
        }
        if (type === "feedback") {
            initiateModal(
                Feedback,
                {
                    id: currentProject.projectId,
                    projectKey: currentProject.projectKey,
                    onSubmit: () => {
                        closeModal();
                        setTimeout(() => {
                            resetProject();
                        }, 1200); // Give the background process time
                    },
                    analyticsLabel: getTerm("potentialProject"),
                },
                { analyticsLabel: "Feedback" }
            );
        }
        if (type === "share") {
            initiateModal(
                ShareModal,
                {
                    type: "project/potential",
                    analyticsLabel: getTerm("potentialProject"),
                },
                { analyticsLabel: getTerm("potentialProject") }
            );
        }
    };

    const resetProject = () => {
        closeModal();
        fetchProject(currentProject.subClientId, "potential", currentProject.projectTypeId);
    };

    const onSalesforceSubmit = () => {
        closeModal();
        resetProject();
    };

    const onRebelGroupSubmit = () => {
        closeModal();
        resetProject();
    };

    return (
        <div id="potential-project" className="main-wrapper">
            <div className="page-content -shadow">
                <div className="content">
                    <div className="flex-header">
                        <h1 className="txt -uppercase -no-margin -tablet-center" style={{ display: "flex", position: "relative" }}>
                            <em>{getTerm("potential")}:&nbsp;</em>
                            <span>{currentProject.name}</span>
                        </h1>
                        <div className="project-links">
                            {currentProject.externalResourceLinks &&
                                currentProject.externalResourceLinks?.map((l, i) => {
                                    return (
                                        <a
                                            key={`link_${l.type}_${i}`}
                                            className="button -text"
                                            href={l.url}
                                            target="_blank"
                                            onClick={() => {
                                                appInsights.trackEvent(
                                                    { name: `External Link - ${capitalizeFirstLetter(l.type)}` },
                                                    {
                                                        label: l.url,
                                                        page: document.title.replace(" - Orgaimi", ""),
                                                    }
                                                );
                                            }}
                                        >
                                            <img src={`/images/icon-${l.type.toLowerCase()}.svg`} className="svg -detail" title={capitalizeFirstLetter(l.type)} width="32" />
                                        </a>
                                    );
                                })}
                            {!currentProject.userFlagged && (
                                <button
                                    className="button -text"
                                    onClick={() => {
                                        triggerModal("feedback");
                                    }}
                                    title="Feedback"
                                >
                                    <FontAwesomeIcon icon={faFlag} />
                                </button>
                            )}
                            {enableShare && (
                                <button
                                    className="button -text"
                                    onClick={() => {
                                        triggerModal("share");
                                    }}
                                    title="Share"
                                >
                                    <FontAwesomeIcon icon={faShareNodes} />
                                </button>
                            )}
                            {enableSalesForce && (
                                <SalesforceButton
                                    disabled={currentProject.hasSalesforceOpportunityCreated}
                                    onClick={() => {
                                        triggerModal("salesforce");
                                    }}
                                />
                            )}
                            {enableRebelGroup && (
                                <RebelGroupButton
                                    disabled={currentProject.hasRebelGroupOpportunityCreated}
                                    onClick={() => {
                                        triggerModal("rebelgroup");
                                    }}
                                />
                            )}
                        </div>
                    </div>
                    {currentProject.isCrossSell && <NotificationBar message="Cross-Sell Opportunity" type="success" icon={faHandHoldingDollar} />}
                    {currentProject.userFlagged && (
                        <NotificationBar type="warning">
                            <FeedbackUndo id={currentProject.projectId} projectKey={currentProject.projectKey} onSubmit={resetProject} />
                        </NotificationBar>
                    )}
                    {currentProject.requiresIndependence && <NotificationBar message="Check Conflicts" type="warning" />}
                    <InfoTable data={currentProject} dataMap={infoPropertiesMap} colCount={6} />

                    <div className="bg -dark">
                        <Comments projectKey={currentProject.projectKey} analyticsLabel={getTerm("potentialProjects")} />
                    </div>

                    {comparableProjects && comparableProjects.length > 0 && (
                        <>
                            <h3>
                                Similar <span data-tooltip={getTerm("tooltipTopPotentialProjects")}>{getTerm("potentialProjects")}</span>
                            </h3>

                            <div className="similar-projects">
                                <PotentialProjects data={comparableProjects} />
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default PotentialProject;
